import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useMemo } from "react";
import moment from "moment";
// @ts-ignore
import { Calendar, momentLocalizer } from "../../components/calendar";
import "./Journal.css";
import { CalendarNotesModal } from "../../components/calendar/CalendarNoteModal";
import { useCalendarController } from "./useCalendarController";
import { KnowledgeBaseHelperTopics, ToolbarFeatures } from "../../types";
import { RiCalendar2Fill } from "react-icons/ri";
import { InsightWrapper2 } from "../insights/container";
import { ToolbarDropDown } from "../../components/design_library/toolbar/ToolbarDropDown";
import { MdBook, MdBuild } from "react-icons/md";

const localizer = momentLocalizer(moment);

interface RebuildPositionsModalProps {
  isOpen: boolean;
  isLoading: boolean;
  month: string;
  tradeCount: number;
  onClose: () => void;
  onRebuild: () => void;
}

const RebuildPositionsModal: React.FC<RebuildPositionsModalProps> = ({
  isOpen,
  isLoading,
  month,
  tradeCount,
  onClose,
  onRebuild,
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay backdropFilter="blur(3px)" />
      <ModalContent bg="var(--bg)" color="var(--white)" borderRadius="md">
        <ModalHeader>
          <HStack spacing={3}>
            <Icon as={MdBuild} color="var(--accent)" boxSize={5} />
            <Text>Rebuild positions?</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <Text color="var(--light-gray)">
              We found {tradeCount} {tradeCount === 1 ? "trade" : "trades"} in{" "}
              {month}, but no position P&L rows for this account. Calendar P&L
              is calculated from compiled positions, so those positions may need
              to be rebuilt from your trades.
            </Text>
            <Text color="var(--light-gray)">
              Rebuilding recalculates the positions and audit rows for the
              selected account, then refreshes this calendar.
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={onClose} isDisabled={isLoading}>
              Not now
            </Button>
            <Button
              leftIcon={<MdBuild />}
              bg="var(--accent)"
              color="white"
              _hover={{ bg: "var(--accent)", opacity: 0.9 }}
              onClick={onRebuild}
              isLoading={isLoading}
              loadingText="Rebuilding"
            >
              Rebuild positions
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const CalendarComponent: React.FC = () => {
  const controller = useCalendarController();

  // Memoize calendar props to prevent unnecessary re-renders
  const calendarProps = useMemo(() => ({
    setDay: (d: Date) => {
      controller.createNote("", moment(d).format("YYYY-MM-DD"));
    },
    day: controller.day,
    localizer,
    events: controller.events,
    notes: controller.notes,
    earnings: controller.earnings,
    expiries: controller.expiries,
    pnlByDate: controller.pnlByDate,
    trades: controller.trades,
    numberOfTradedDays: controller.numberOfTradedDays,
    pnlThisMonth: controller.pnlThisMonth,
    startAccessor: "start",
    endAccessor: "end",
    style: { minHeight: "85vh", height: "100%", width: "100%" },
    onNavigate: (date: Date) => {
      controller.setMonth(moment.utc(date).format("MMM YYYY"));
      controller.getNotes(moment.utc(date).format("MMM YYYY"));
    },
  }), [
    controller.day,
    controller.events,
    controller.notes,
    controller.earnings,
    controller.expiries,
    controller.pnlByDate,
    controller.trades,
    controller.numberOfTradedDays,
    controller.pnlThisMonth,
    controller.createNote,
    controller.setMonth,
    controller.getNotes
  ]);

  // Memoize modal props to prevent unnecessary re-renders
  const modalProps = useMemo(() => ({
    isOpen: controller.isOpen,
    onClose: () => {
      controller.setIsOpen(false);
      controller.getNotes(controller.month);
    },
    note: controller.selectedNote
      ? {
          content: controller.selectedNote.content,
          date: moment
            .utc(controller.selectedNote.calendarDateReference)
            .toDate(),
          id: controller.selectedNote.id,
        }
      : { content: "", date: moment.utc(controller.day, "DD MMM YYYY").toDate(), id: "" },
    date: controller.selectedNote?.calendarDateReference
      ? moment.utc(controller.selectedNote.calendarDateReference).toDate()
      : moment.utc(controller.day, "DD MMM YYYY").toDate(),
    expiries: controller.expiriesForThisDate as any,
    trades: controller.tradesForThisDate as any,
    earnings: controller.earningsForThisDate,
  }), [
    controller.isOpen,
    controller.selectedNote,
    controller.expiriesForThisDate,
    controller.tradesForThisDate,
    controller.earningsForThisDate,
    controller.month,
    controller.getNotes,
    controller.setIsOpen,
    controller.day
  ]);

  return (
    <Box w="full">
      {controller.loading && (
        <Box
          pos="fixed"
          bg="var(--bg)"
          w="100vw"
          h="100vh"
          top="0"
          left="0"
          zIndex={99}
          opacity="0.2"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Spinner opacity="1" size="xl" />
        </Box>
      )}

      <HStack className="grid-toolbar-hook" mx="2" mb={2} spacing={2} wrap={"wrap"}>
        <HStack className="grid-toolbar-select" spacing={2}>
        
        </HStack>
        <ToolbarDropDown
          className="tool-bar-select"
          placeholder={
            <HStack color="var(--white)">
              <MdBook />
              <Text display={{ base: "none", md: "block" }}>Book</Text>
            </HStack>
          }
          options={(controller.books || []).map((b) => ({ value: b, label: b }))}
          value={
            controller.selectedBook && controller.selectedBook !== "All"
              ? { value: controller.selectedBook, label: controller.selectedBook }
              : { value: "All", label: "All" }
          }
          onChange={(e: any) => controller.setSelectedBook(e?.value === "All" ? undefined : e?.value)}
        />
      </HStack>

      <Flex flex={1} px={2}>
        <CalendarNotesModal {...modalProps} />
        <RebuildPositionsModal
          isOpen={controller.showRebuildPositionsModal}
          isLoading={controller.isRebuildingPositions}
          month={controller.rebuildPositionsPrompt?.month || controller.month}
          tradeCount={controller.rebuildPositionsPrompt?.tradeCount || 0}
          onClose={controller.dismissRebuildPositionsModal}
          onRebuild={controller.rebuildPositionsForCalendar}
        />
        <Box
          mt="10px"
          borderRadius="lg"
          boxShadow="lg"
          p={4}
          overflow="hidden"
          w="full"
          bg="var(--bg)"
          border="1px solid var(--bg)"
          userSelect="none"
        >
          <Calendar {...calendarProps} />
          <Text fontSize="sm" color="var(--light-gray)" mt="15px">
            *Earnings dates are provisional until confirmed by the company.
            These are updated once per day.
          </Text>
        </Box>
      </Flex>
    </Box>
  );
};

export default React.memo(CalendarComponent);
